import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { authorCollectingSocietyService } from "../services/authorCollectingSocietyService";
import {
  AuthorCollectingSocietyValuesInit,
  getAuthorCollectingSocietyValues,
  AuthorCollectingSocietyValuesType,
  AuthorCollectingSocietyErrorsType,
  authorCollectingSocietyDisplayId,
} from "./AuthorCollectingSocietyCommonStore";
import { AuthorCollectingSociety } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getCollectiveManagementSocietyValues,
  CollectiveManagementSocietyValuesType,
} from "../../collectiveManagementSociety/store/CollectiveManagementSocietyCommonStore";
import {
  getTerritoryValues,
  TerritoryValuesType,
} from "../../territory/store/TerritoryCommonStore";
import { collectiveManagementSocietyService } from "../../collectiveManagementSociety/services/collectiveManagementSocietyService";
import { territoryService } from "../../territory/services/territoryService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class AuthorCollectingSocietyCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: AuthorCollectingSocietyErrorsType = {};
  service = authorCollectingSocietyService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: AuthorCollectingSociety | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = AuthorCollectingSocietyValuesInit();
  collectingSocietyError: string | undefined;
  collectingSocietyService = collectiveManagementSocietyService;
  collectingSocietySearchValue: string = "";
  collectingSocietyOptions: CollectiveManagementSocietyValuesType[] = [];
  memberNumberError: string | undefined;
  territoriesError: string | undefined;
  territoriesService = territoryService;
  territoriesSearchValue: string = "";
  territoriesOptions: TerritoryValuesType[] = [];

  private customValidators: CustomValidator<AuthorCollectingSocietyValuesType>[] =
    [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<AuthorCollectingSocietyValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<AuthorCollectingSocietyValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return authorCollectingSocietyDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.AuthorCollectingSocietySingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity

      if (params.callerData?.fieldName === "collectingSociety") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showCollectingSociety = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.collectiveManagementSocietyOne({
            id: params.returnData.resultId,
          });
          const collectiveManagementSociety =
            result.collectiveManagementSocietyOne;
          this.entityValues.showCollectingSociety = {
            displayId: collectiveManagementSociety.displayId,
            id: collectiveManagementSociety.id,
          };
          this.setCollectingSocietyOptions([
            getCollectiveManagementSocietyValues(
              collectiveManagementSociety,
              this.langStore
            ),
          ]);
        }
      } else if (params.callerData?.fieldName === "territories") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showTerritories.push({
            displayId,
            id: params.returnData.entity.id,
          });
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.territoryOne({
            id: params.returnData.resultId,
          });
          const territory = result.territoryOne;
          this.entityValues.showTerritories.push({
            displayId: territory.displayId,
            id: territory.id,
          });
          this.setTerritoriesOptions([
            getTerritoryValues(territory, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupAuthorCollectingSocietyLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  setCollectingSocietyOptions(values: CollectiveManagementSocietyValuesType[]) {
    this.collectingSocietyOptions = values;
  }

  *loadCollectingSocietyValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("CollectiveManagementSociety");
    const allOptions = cache.getOptions(
      "CollectiveManagementSociety"
    ) as CollectiveManagementSocietyValuesType[];

    const searchValue = this.collectingSocietySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setCollectingSocietyOptions(filtered);
  }

  getCollectingSocietyOptions() {
    return this.collectingSocietyOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCollectingSocietyValue(value: string) {
    this.collectingSocietySearchValue = value;
    yield this.loadCollectingSocietyValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCollectingSociety(
    value: { displayId: string; id: string } | null
  ) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCollectiveManagementSociety(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "collectingSociety"
      );
    } else {
      if (value) {
        this.entityValues.showCollectingSociety = value;
        this.entityValues.__collectingSociety = value.displayId;
      }
    }
  }

  getMemberNumberValue(): string {
    return this.entityValues.memberNumber;
  }

  setMemberNumberValue(value: string) {
    this.entityValues.memberNumber = value;
  }

  setTerritoriesOptions(values: TerritoryValuesType[]) {
    this.territoriesOptions = values;
  }

  *loadTerritoriesValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Territory");
    const allOptions = cache.getOptions("Territory") as TerritoryValuesType[];

    const searchValue = this.territoriesSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setTerritoriesOptions(filtered);
  }

  getTerritoriesOptions() {
    return this.territoriesOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setTerritoriesValue(value: string) {
    this.territoriesSearchValue = value;
    yield this.loadTerritoriesValues();
  }

  setSelectedTerritories(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewTerritory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "territories"
      );
    } else {
      this.entityValues.showTerritories = values;
      this.entityValues.__territories = values.map((v) => v.displayId);
    }
  }

  addNewCollectiveManagementSociety(
    notFound: string | undefined,
    callerFieldName?: string
  ) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "AuthorCollectingSociety",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/collectiveManagementSociety/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewTerritory(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "AuthorCollectingSociety",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/territory/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = AuthorCollectingSocietyValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
    this.collectingSocietySearchValue = "";
    this.collectingSocietyOptions = [];
    this.territoriesSearchValue = "";
    this.territoriesOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(
    values: AuthorCollectingSocietyValuesType
  ): AuthorCollectingSocietyErrorsType {
    const errors: AuthorCollectingSocietyErrorsType = {};
    this.clearErrors();
    this.collectingSocietyError = undefined;
    if (
      values.showCollectingSociety === null ||
      values.showCollectingSociety.id === ""
    ) {
      errors.collectingSociety = {
        message: this.langStore.mandatoryField,
        fieldLabel:
          this.langStore.AuthorCollectingSocietyCollectingSocietyLabel,
      };
    }
    values.memberNumber = values.memberNumber.trim();
    if (values.memberNumber === "") {
      errors.memberNumber = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.AuthorCollectingSocietyMemberNumberLabel,
      };
    }
    if (values.showTerritories.length === 0) {
      errors.territories = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.AuthorCollectingSocietyTerritoriesLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "AuthorCollectingSocietyCreateStore"
    );
  }

  *duplicatesAuthorCollectingSociety(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const authorCollectingSocietyInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (authorCollectingSocietyInstance) {
      this.entityValues = getAuthorCollectingSocietyValues(
        authorCollectingSocietyInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la vista del registro creado.
      this.rootStore.router.replace(
        `/authorCollectingSociety/view/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewAuthorCollectingSociety(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewAuthorCollectingSociety(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      memberNumber: this.entityValues.memberNumber,
      collectingSociety: this.entityValues.showCollectingSociety?.id || null,
      territories: (this.entityValues.showTerritories || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getAuthorCollectingSocietyValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = AuthorCollectingSocietyValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    // Subentidad sin alta standalone: se persiste por el create/update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesAuthorCollectingSociety(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.memberNumber = initialDisplayId;
      }

      yield this.loadCollectingSocietyValues();
      yield this.loadTerritoriesValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
